/** Full-page states shown before the app shell: loading, sign-in, errors. */
import { type ReactNode, useEffect, useState } from "react";

import { Panel } from "../components/ui/Panel";
import { ClockIcon } from "../components/ui/Icons";
import { reloadPage } from "./reload";

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-page p-4">
      <Panel className="w-full max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex size-10 items-center justify-center rounded-icon bg-brand text-white">
          <ClockIcon />
        </div>
        {children}
      </Panel>
    </div>
  );
}

const primaryButton =
  "mt-6 h-11 cursor-pointer rounded-pill bg-cta-bg px-6 font-semibold text-cta-fg hover:opacity-90";

// After this long, loading is more likely stuck than slow.
export const SLOW_AFTER_MS = 20_000;

export function LoadingScreen({ onReload = reloadPage }: { onReload?: () => void }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSlow(true);
    }, SLOW_AFTER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  return (
    <Centered>
      <p role="status" className="text-muted">
        {slow ? "This is taking longer than usual." : "Loading Axon Time…"}
      </p>
      {slow ? (
        <button type="button" className={primaryButton} onClick={onReload}>
          Reload
        </button>
      ) : null}
    </Centered>
  );
}

/** Opening the app with a sign-in to renew: on the way to Microsoft and back (ADR 0039). */
export function RenewingScreen() {
  return (
    <Centered>
      <p role="status" className="text-muted">
        Renewing your sign-in…
      </p>
    </Centered>
  );
}

/** Why going to Microsoft didn't start, under the button that tried (#865). */
function CantGo({ failed }: { failed: string | null | undefined }) {
  return failed ? (
    <p role="alert" className="mt-3 text-sm text-status-needs-client-fg">
      {failed}
    </p>
  ) : null;
}

export function SignInScreen({
  onSignIn,
  failed,
}: {
  onSignIn: () => void;
  /** Why the sign-in didn't start, if it didn't. */
  failed?: string | null;
}) {
  return (
    <Centered>
      <h1 className="text-2xl font-semibold tracking-tight">Axon Time</h1>
      <p className="mt-2 text-muted">Sign in with your Axon Microsoft account.</p>
      <button type="button" className={primaryButton} onClick={onSignIn}>
        Sign in with Microsoft
      </button>
      <CantGo failed={failed} />
    </Centered>
  );
}

export function MessageScreen({
  title,
  message,
  action,
  failed,
}: {
  title: string;
  message: string;
  action?: { label: string; onClick: () => void };
  /** Why the action, going to Microsoft, didn't start, if it didn't. */
  failed?: string | null;
}) {
  return (
    <Centered>
      <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
      <p role="alert" className="mt-2 text-muted">
        {message}
      </p>
      {action ? (
        <button type="button" className={primaryButton} onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
      <CantGo failed={failed} />
    </Centered>
  );
}
